import HeadingSmall from '@/components/layout/HeadingSmall';
import { Button } from '@/components/ui/shadcn/button';
import { Switch } from '@/components/ui/shadcn/switch';
import { useTranslations } from '@/hooks/useTranslations';
import { type User } from '@/types';
import { Transition } from '@headlessui/react';
import { useForm } from '@inertiajs/react';
import { AtSign, BadgeEuro, Bug, Calculator, CalendarDays, ClipboardList, FileSignature, RefreshCw, UserCheck } from 'lucide-react';
import React, { type FormEventHandler } from 'react';
import { route } from 'ziggy-js';

const NOTIFICATION_TYPES = [
    'mentions',
    'work_order_assignment',
    'work_order_created',
    'bug_reports',
    'vacation_request',
    'status_change',
    'calculation_request_created',
    'calculation_request_status_change',
    'service_request_approval',
    'lead_offer_approval',
] as const;
type NotificationType = (typeof NOTIFICATION_TYPES)[number];
type NotificationPrefs = Record<NotificationType, { email: boolean; in_app: boolean }>;

const NOTIFICATION_ICONS: Record<NotificationType, React.ReactNode> = {
    mentions: <AtSign className="h-5 w-5" />,
    work_order_assignment: <UserCheck className="h-5 w-5" />,
    work_order_created: <ClipboardList className="h-5 w-5" />,
    bug_reports: <Bug className="h-5 w-5" />,
    vacation_request: <CalendarDays className="h-5 w-5" />,
    status_change: <RefreshCw className="h-5 w-5" />,
    calculation_request_created: <Calculator className="h-5 w-5" />,
    calculation_request_status_change: <RefreshCw className="h-5 w-5" />,
    service_request_approval: <FileSignature className="h-5 w-5" />,
    lead_offer_approval: <BadgeEuro className="h-5 w-5" />,
};

const DEFAULT_PREFS: NotificationPrefs = {
    mentions: { email: true, in_app: true },
    work_order_assignment: { email: true, in_app: true },
    work_order_created: { email: true, in_app: true },
    bug_reports: { email: true, in_app: true },
    vacation_request: { email: true, in_app: true },
    status_change: { email: true, in_app: true },
    calculation_request_created: { email: true, in_app: true },
    calculation_request_status_change: { email: true, in_app: true },
    service_request_approval: { email: true, in_app: true },
    lead_offer_approval: { email: true, in_app: true },
};

interface NotificationSettingsTabProps {
    user: User;
}

export default function NotificationSettingsTab({ user }: NotificationSettingsTabProps) {
    const { t } = useTranslations();

    const storedPrefs = (user.notification_preferences ?? {}) as Partial<NotificationPrefs>;
    const mergedPrefs = NOTIFICATION_TYPES.reduce((acc, type) => {
        acc[type] = {
            email: storedPrefs[type]?.email ?? DEFAULT_PREFS[type].email,
            in_app: storedPrefs[type]?.in_app ?? DEFAULT_PREFS[type].in_app,
        };
        return acc;
    }, {} as NotificationPrefs);

    const { data, setData, patch, processing, recentlySuccessful } = useForm<{ notification_preferences: NotificationPrefs }>({
        notification_preferences: mergedPrefs,
    });

    const togglePref = (type: NotificationType, channel: 'email' | 'in_app', value: boolean) => {
        setData('notification_preferences', {
            ...data.notification_preferences,
            [type]: {
                ...data.notification_preferences[type],
                [channel]: value,
            },
        });
    };

    const submit: FormEventHandler = (e) => {
        e.preventDefault();

        patch(route('employees.notification-preferences.update', user.id), {
            preserveScroll: true,
            preserveState: true,
        });
    };

    return (
        <form onSubmit={submit} className="space-y-6">
            <HeadingSmall
                title={t('employees.notifications.title', 'Notification preferences')}
                description={t('employees.notifications.description', 'Manage which email and in-app notifications this employee receives.')}
            />

            <div className="overflow-hidden rounded-xl border">
                {/* Header row */}
                <div className="bg-muted/40 grid grid-cols-[1fr_88px_88px] items-center border-b px-4 py-2">
                    <div />
                    <span className="text-muted-foreground text-center text-xs font-medium">
                        {t('settings.profile.notifications_email', 'Email')}
                    </span>
                    <span className="text-muted-foreground text-center text-xs font-medium">
                        {t('settings.profile.notifications_in_app', 'In-app')}
                    </span>
                </div>

                {/* Notification rows */}
                {NOTIFICATION_TYPES.map((type, idx) => (
                    <div
                        key={type}
                        className={`grid grid-cols-[1fr_88px_88px] items-center px-4 py-4 ${idx < NOTIFICATION_TYPES.length - 1 ? 'border-b' : ''}`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="bg-muted text-muted-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                                {NOTIFICATION_ICONS[type]}
                            </div>
                            <div>
                                <p className="text-sm font-semibold">{t(`settings.profile.notifications_${type}`, type)}</p>
                                <p className="text-muted-foreground text-xs">{t(`settings.profile.notifications_${type}_description`, '')}</p>
                            </div>
                        </div>
                        <div className="flex justify-center">
                            <Switch checked={data.notification_preferences[type].email} onCheckedChange={(v) => togglePref(type, 'email', v)} />
                        </div>
                        <div className="flex justify-center">
                            <Switch checked={data.notification_preferences[type].in_app} onCheckedChange={(v) => togglePref(type, 'in_app', v)} />
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex items-center gap-4">
                <Button disabled={processing}>{t('common.save', 'Save')}</Button>

                <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                >
                    <p className="text-muted-foreground text-sm">{t('common.saved', 'Saved')}</p>
                </Transition>
            </div>
        </form>
    );
}
